import {
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComparisonOperations,
  Operations,
} from '../../../common/constants/constants';
import { MetricFormatTypeId } from '../../../common/graphql/generated/graphql-sdk';
import {
  FIELD_LABEL_SX,
  FilterPopoverShell,
  MEDIUM_FONT_FAMILY,
} from './FilterPopoverShell';
import {
  daysFromYearsMonths,
  DEFAULT_VALUE_FILTER_OPERATION,
  getValueFilterInputKind,
  isValueFilterActive,
  parseNonNegativeInt,
  parseValueFilterThreshold,
  VALUE_FILTER_OPERATIONS,
  yearsMonthsFromDays,
  type PbsDrillDownValueFilter,
} from './services/pbsDrillDownValueFilter';

const OP_SYMBOL: Record<ComparisonOperations, string> = {
  [Operations.GREATER_THAN]: '>',
  [Operations.GREATER_THAN_OR_EQUAL_TO]: '≥',
  [Operations.LESS_THAN]: '<',
  [Operations.LESS_THAN_OR_EQUAL_TO]: '≤',
  [Operations.EQUAL]: '=',
};

export interface PbsValueFilterPopoverProps {
  formatType: MetricFormatTypeId | undefined;
  /**
   * Formats a raw threshold the way Value cells are shown. Accepted for callers
   * that still pass it; the popover no longer renders an inline preview.
   */
  formatThreshold?: (raw: number) => string;
  value: PbsDrillDownValueFilter;
  onChange: (next: PbsDrillDownValueFilter) => void;
  /** The measure's display name, shown in the popover title and field label instead of the generic "Value". */
  measureLabel?: string;
}

/**
 * Filter control for the Value column header: icon opens a popover whose inputs
 * match the measure's display format (years/months for tenure, % for percentages, …).
 */
export const PbsValueFilterPopover: React.FC<PbsValueFilterPopoverProps> = ({
  formatType,
  value,
  onChange,
  measureLabel,
}) => {
  const { t } = useTranslation();
  const inputKind = getValueFilterInputKind(formatType);

  const [draftOp, setDraftOp] = useState<ComparisonOperations>(value.operation);
  const [draftNumber, setDraftNumber] = useState('');
  const [draftYears, setDraftYears] = useState('');
  const [draftMonths, setDraftMonths] = useState('');

  const seedDraftFromValue = (filter: PbsDrillDownValueFilter) => {
    setDraftOp(filter.operation);
    if (inputKind === 'tenureYearsMonths') {
      if (filter.tenureEqual != null) {
        setDraftYears(String(filter.tenureEqual.years));
        setDraftMonths(String(filter.tenureEqual.months));
      } else if (filter.threshold != null) {
        const { years, months } = yearsMonthsFromDays(filter.threshold);
        setDraftYears(String(Math.abs(years)));
        setDraftMonths(String(months));
      } else {
        setDraftYears('');
        setDraftMonths('');
      }
      setDraftNumber('');
    } else if (filter.threshold != null) {
      setDraftNumber(String(filter.threshold));
      setDraftYears('');
      setDraftMonths('');
    } else {
      setDraftNumber('');
      setDraftYears('');
      setDraftMonths('');
    }
  };

  const draftFilter: PbsDrillDownValueFilter = useMemo(() => {
    if (inputKind === 'tenureYearsMonths') {
      const years = parseNonNegativeInt(draftYears);
      const months = parseNonNegativeInt(draftMonths);
      if (years == null && months == null) {
        return { operation: draftOp, threshold: null, tenureEqual: null };
      }
      const y = years ?? 0;
      const m = months ?? 0;
      if (draftOp === Operations.EQUAL) {
        return {
          operation: draftOp,
          threshold: daysFromYearsMonths(y, m),
          tenureEqual: { years: y, months: m },
        };
      }
      return {
        operation: draftOp,
        threshold: daysFromYearsMonths(y, m),
        tenureEqual: null,
      };
    }
    return {
      operation: draftOp,
      threshold: parseValueFilterThreshold(draftNumber),
      tenureEqual: null,
    };
  }, [inputKind, draftOp, draftYears, draftMonths, draftNumber]);

  const handleApply = () => onChange(draftFilter);

  const handleClear = () => {
    const cleared: PbsDrillDownValueFilter = {
      operation: DEFAULT_VALUE_FILTER_OPERATION,
      threshold: null,
      tenureEqual: null,
    };
    onChange(cleared);
    seedDraftFromValue(cleared);
  };

  const active = isValueFilterActive(value);

  const valueFieldLabel =
    measureLabel ??
    t('pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterLabel', {
      defaultValue: 'Value',
    });

  const title = measureLabel
    ? t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterTitleWithMeasure',
        { defaultValue: 'Filter {{measure}}', measure: measureLabel }
      )
    : t('pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterTitle', {
        defaultValue: 'Filter Value',
      });

  const triggerAriaLabel = measureLabel
    ? t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterOpenWithMeasure',
        { defaultValue: 'Filter by {{measure}}', measure: measureLabel }
      )
    : t('pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterOpen', {
        defaultValue: 'Filter by Value',
      });

  const endAdornment =
    inputKind === 'percentage' ? (
      <InputAdornment position="end">%</InputAdornment>
    ) : inputKind === 'multiple' ? (
      <InputAdornment position="end">×</InputAdornment>
    ) : undefined;

  return (
    <Box data-pbs-drilldown-tour="modal-value-filter">
      <FilterPopoverShell
        active={active}
        triggerAriaLabel={triggerAriaLabel}
        title={title}
        onOpen={() => seedDraftFromValue(value)}
        onApply={handleApply}
        onClear={handleClear}
        applyDisabled={!isValueFilterActive(draftFilter)}
        clearDisabled={!active && !isValueFilterActive(draftFilter)}
        applyLabel={t(
          'pages:dashboards.peopleBalanceSheet.cellDrillDown.filterApply',
          { defaultValue: 'Apply' }
        )}
        clearLabel={t(
          'pages:dashboards.peopleBalanceSheet.cellDrillDown.filterClear',
          { defaultValue: 'Clear' }
        )}
      >
        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            {t(
              'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterOperation',
              { defaultValue: 'Comparison' }
            )}
          </Typography>
          <FormControl fullWidth size="small" variant="standard">
            <Select
              value={draftOp}
              onChange={(e) =>
                setDraftOp(e.target.value as ComparisonOperations)
              }
            >
              {VALUE_FILTER_OPERATIONS.map((op) => (
                <MenuItem key={op} value={op}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 20,
                      fontFamily: MEDIUM_FONT_FAMILY,
                      color: 'primary.main',
                    }}
                  >
                    {OP_SYMBOL[op]}
                  </Box>
                  {t(`common:operations.${op}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {inputKind === 'tenureYearsMonths' ? (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                {t(
                  'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterYears',
                  { defaultValue: 'Years' }
                )}
              </Typography>
              <TextField
                size="small"
                value={draftYears}
                onChange={(e) => setDraftYears(e.target.value)}
                inputProps={{ inputMode: 'numeric' }}
                autoFocus
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                {t(
                  'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterMonths',
                  { defaultValue: 'Months' }
                )}
              </Typography>
              <TextField
                size="small"
                value={draftMonths}
                onChange={(e) => setDraftMonths(e.target.value)}
                inputProps={{ inputMode: 'numeric' }}
                fullWidth
              />
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              {valueFieldLabel}
            </Typography>
            <TextField
              size="small"
              sx={{ width: 140 }}
              autoFocus
              value={draftNumber}
              onChange={(e) => setDraftNumber(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment }}
              placeholder={
                inputKind === 'percentage'
                  ? t(
                      'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterPlaceholderPercent',
                      { defaultValue: 'e.g. 50' }
                    )
                  : t(
                      'pages:dashboards.peopleBalanceSheet.cellDrillDown.valueFilterPlaceholder',
                      { defaultValue: 'e.g. 2' }
                    )
              }
            />
          </Box>
        )}
      </FilterPopoverShell>
    </Box>
  );
};
